/**
 * Reusable share button component.
 * Posts a share card to the Discord channel via the bot.
 */

import { APIManager } from '../../managers';
import { getChannelId } from '../../discord';
import { ToastManager } from './Toast';

export type ShareType = 'item' | 'strand' | 'build' | 'daily';

/** Create a share button element. Caller appends it to the DOM. */
export function createShareButton(
  type: ShareType,
  id?: string,
  label = 'Share',
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'share-btn';
  btn.textContent = label;
  btn.addEventListener('click', () => handleShare(btn, type, id));
  return btn;
}

/** Render inline share button HTML (for use in template strings). */
export function shareButtonHTML(type: ShareType, id?: string, label = 'Share'): string {
  return `<button class="share-btn" data-share-type="${type}" ${id ? `data-share-id="${id}"` : ''}>${label}</button>`;
}

/** Attach click handlers to all share buttons within a container. */
export function bindShareButtons(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>('.share-btn[data-share-type]').forEach(btn => {
    btn.addEventListener('click', () => {
      const type = btn.dataset.shareType as ShareType;
      const id = btn.dataset.shareId;
      handleShare(btn, type, id);
    });
  });
}

async function handleShare(btn: HTMLButtonElement, type: ShareType, id?: string): Promise<void> {
  const channelId = getChannelId();
  if (!channelId) {
    ToastManager.show({ type: 'error', message: 'Not in a Discord channel' });
    return;
  }

  btn.classList.add('loading');
  btn.disabled = true;

  try {
    const result = await APIManager.shareToChannel(type, channelId, id);
    if (result.success) {
      ToastManager.show({ type: 'success', message: 'Shared to channel!' });
    } else {
      ToastManager.show({ type: 'error', message: result.error ?? 'Share failed' });
    }
  } catch {
    ToastManager.show({ type: 'error', message: 'Share failed' });
  } finally {
    btn.classList.remove('loading');
    btn.disabled = false;
  }
}
